import { useState, useContext, useEffect } from 'react';
import { Box, Button, IconButton, InputAdornment, OutlinedInput, TextField, Typography, useMediaQuery } from '@mui/material';
import { useHistory } from 'react-router';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import * as JWT from 'jsonwebtoken';
// Our Components
import { AuthContext } from 'context/auth-context';
import { Set_Name } from 'resolvers/Set_Name';
import { Token } from 'resolvers/Token';
import { useForm } from 'Hooks/useForm';
import ErrorAlert from 'components/shared/ErrorAlert';
// Utils and Helpers
import { APP_ROUTES } from 'routes';
import { LOGIN_ERROR_MESSAGES } from 'helpers/constants';
// Services
import { Login } from 'lib/api';
// Assets
import '../Login.css';

const FirstStepLogin = ({ nextStep, loginError, isLoading, setIsLoading }) => {
    const history = useHistory();
    const matches = useMediaQuery('(min-height:750px)');
    const { setPreUser, loginUser, setDecodeState } = useContext(AuthContext);
    const [formValues, handleInputChange] = useForm({
        email: '',
        password: ''
    });
    const [showPassword, setShowPassword] = useState();
    const [errorMessage, setErrorMessage] = useState('');
    const [errorType, setErrorType] = useState('');

    const onEnter = event => {
        if (event.keyCode === 13) {
            preLogin();
        }
    };

    const preLogin = async () => {
        setPreUser(formValues);

        try {
            setIsLoading(true);

            const response = await Login.loginUser(formValues);
            if (response.success) {
                setIsLoading(false);
                if (response.token) {
                    loginUser(response);
                    Token(response);
                    Set_Name(response);
                    const tokenUser = JWT.decode(response.token);
                    setDecodeState({
                        ...tokenUser,
                        loaded: true
                    });

                    return history.replace(APP_ROUTES.DASHBOARD);
                }
                nextStep();
            }
        } catch (error) {
            setIsLoading(false);
            setErrorMessage(error?.response?.data?.message);
            setErrorType(error?.response?.data?.type);
        }
    };
    useEffect(() => {
        if (loginError) setErrorMessage(LOGIN_ERROR_MESSAGES[loginError]);
    }, [loginError]);

    return (
        <Box display="flex" flexDirection="column">
            <Box
                marginTop={matches ? '48px' : '24px'}
                sx={{
                    fontWeight: 'bold',
                    fontSize: '23px'
                }}>
                Welcome back!
            </Box>
            <Box marginTop="12px" marginBottom="16px">
                <Typography variant="subtitle1">
                    Please fill out the information below to access your Paystand Spend account today.
                </Typography>
            </Box>
            {errorMessage && (
                <ErrorAlert>
                    {errorMessage}{' '}
                    {errorType === 'Auth/MultipleCustomers' && (
                        <>
                            <a href={process.env.REACT_APP_AR_FRONTEND_URL}>here</a>.
                        </>
                    )}
                </ErrorAlert>
            )}
            <Box marginTop={matches ? '32px' : '16px'}>
                <TextField
                    name="email"
                    size="small"
                    required={true}
                    placeholder="Email address*"
                    className="input-login-form"
                    onChange={handleInputChange}
                    onKeyDown={event => onEnter(event)}
                />
                <OutlinedInput
                    name="password"
                    className="input-login-form"
                    sx={{ mt: 2, mb: '4px' }}
                    placeholder="Password*"
                    size="small"
                    onChange={handleInputChange}
                    required
                    onKeyDown={event => onEnter(event)}
                    type={showPassword ? 'text' : 'password'}
                    endAdornment={
                        <InputAdornment position="end">
                            <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end">
                                {!showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </InputAdornment>
                    }
                />
                <Box display="flex" justifyContent="flex-end">
                    <Typography variant="customText" onClick={() => history.push(APP_ROUTES.RESET)}>
                        Forgot your Password?
                    </Typography>
                </Box>
            </Box>
            <Box marginTop="24px">
                <Button
                    className="btn-rounded"
                    variant="contained"
                    fullWidth
                    disabled={isLoading}
                    onClick={!isLoading ? () => preLogin() : null}>
                    {isLoading ? 'Waiting' : 'Log In'}
                </Button>
            </Box>
        </Box>
    );
};

export default FirstStepLogin;
